import type { AppData } from '../data/types';
import { buildTree, type TreeNode } from '../data/transforms';

const typeIcon: Record<TreeNode['type'], string> = {
  platform: '🏛️',
  suite: '📦',
  product: '🧩',
  module: '🗂️',
  navigation: '📊',
  kpi: '📈',
};

const typeColor: Record<TreeNode['type'], string> = {
  platform: 'text-slate-900 font-semibold',
  suite: 'text-pulse-800 font-semibold',
  product: 'text-slate-800 font-medium',
  module: 'text-slate-700',
  navigation: 'text-slate-600',
  kpi: 'text-slate-500',
};

function renderNode(node: TreeNode, depth: number, expandedByDefault: number): string {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = depth < expandedByDefault;
  const pad = depth * 18;
  const meta = node.meta
    ? Object.entries(node.meta)
        .filter(([_, v]) => v)
        .map(([k, v]) => `<span class="ml-2 text-[10px] text-slate-400">${k}: ${v}</span>`)
        .join('')
    : '';

  if (!hasChildren) {
    return `
      <div class="flex items-center py-1 hover:bg-slate-50 ${typeColor[node.type]}" style="padding-left:${pad + 24}px">
        <span class="text-xs mr-2">${typeIcon[node.type]}</span>
        <span class="text-sm">${node.label}</span>
        ${meta}
      </div>
    `;
  }

  return `
    <details ${isOpen ? 'open' : ''} class="border-l border-transparent hover:border-slate-200">
      <summary class="flex items-center py-1.5 cursor-pointer hover:bg-slate-50 select-none ${typeColor[node.type]}" style="padding-left:${pad}px">
        <span class="text-slate-400 mr-1 text-xs w-4">▸</span>
        <span class="text-xs mr-2">${typeIcon[node.type]}</span>
        <span class="text-sm">${node.label}</span>
        ${node.count !== undefined ? `<span class="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">${node.count}</span>` : ''}
      </summary>
      <div>
        ${node.children!.map((c) => renderNode(c, depth + 1, expandedByDefault)).join('')}
      </div>
    </details>
  `;
}

export function renderArbol(container: HTMLElement, data: AppData): void {
  const tree = buildTree(data.metrics, data.skus);

  container.innerHTML = `
    <div class="p-6 space-y-4">
      <div class="flex items-center gap-3">
        <input
          id="tree-filter"
          type="search"
          placeholder="Buscar nodo (suite, producto, KPI…)"
          class="flex-1 max-w-md px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-pulse-400"
        />
        <button id="tree-expand-all" class="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100">Expandir todo</button>
        <button id="tree-collapse-all" class="text-xs px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-100">Colapsar todo</button>
        <span class="text-xs text-slate-500 ml-auto">${tree.children?.length ?? 0} suites · ${data.metrics.length} KPIs</span>
      </div>
      <div id="tree" class="bg-white rounded-xl border border-slate-200 p-3 text-sm font-sans max-h-[calc(100vh-220px)] overflow-auto">
        ${renderNode(tree, 0, 2)}
      </div>
    </div>
  `;

  const treeEl = container.querySelector('#tree') as HTMLElement;
  const filter = container.querySelector('#tree-filter') as HTMLInputElement;

  filter.addEventListener('input', () => {
    const q = filter.value.trim().toLowerCase();
    if (!q) {
      treeEl.querySelectorAll('details, div').forEach((el) => ((el as HTMLElement).style.display = ''));
      return;
    }
    const matches = (el: HTMLElement): boolean => {
      if (el.tagName === 'DETAILS') {
        let any = false;
        el.querySelectorAll(':scope > div > *').forEach((child) => {
          if (matches(child as HTMLElement)) any = true;
        });
        if (any) (el as HTMLDetailsElement).open = true;
        el.style.display = any || el.textContent!.toLowerCase().includes(q) ? '' : 'none';
        return any || el.textContent!.toLowerCase().includes(q);
      } else {
        const m = el.textContent!.toLowerCase().includes(q);
        el.style.display = m ? '' : 'none';
        return m;
      }
    };
    treeEl.querySelectorAll(':scope > *').forEach((el) => matches(el as HTMLElement));
  });

  (container.querySelector('#tree-expand-all') as HTMLButtonElement).addEventListener('click', () => {
    treeEl.querySelectorAll('details').forEach((d) => ((d as HTMLDetailsElement).open = true));
  });
  (container.querySelector('#tree-collapse-all') as HTMLButtonElement).addEventListener('click', () => {
    treeEl.querySelectorAll('details').forEach((d) => ((d as HTMLDetailsElement).open = false));
  });
}
